import { focusManager, QueryClientContext } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';

import { agentFeedQueryKey } from '@/lib/agent-feed/hooks/useAgentFeed';
import {
  invalidateCrmQueries,
  wasCrmInvalidatedRecently,
} from '@/lib/crm/hooks/useCRMEventPolling';
import { logger } from '@/lib/logger';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';

import { useUserSleepState } from './useUserSleepState';

/**
 * Must stay >= the reconnect + full_refresh_required round-trip so the wake
 * wave and a server-driven full-refresh collapse into one invalidation via
 * the shared lastCrmInvalidationAt timestamp.
 */
const WAKE_REFRESH_COALESCE_MS = 30_000;

export function useSleepOrchestration({
  orgId,
}: {
  orgId: number | null;
}): boolean {
  const asleep = useUserSleepState();
  const queryClient = useContext(QueryClientContext);
  const prevAsleepRef = useRef(asleep);
  // True while this hook holds focusManager pinned unfocused, so the unmount
  // cleanup below knows whether it must restore default focus tracking.
  const focusPinnedRef = useRef(false);

  useEffect(() => {
    const wasAsleep = prevAsleepRef.current;
    prevAsleepRef.current = asleep;

    if (asleep) {
      focusManager.setFocused(false);
      focusPinnedRef.current = true;
      logger.info('[activity] tab asleep', { orgId });
      return;
    }

    if (wasAsleep) {
      if (queryClient) {
        // CRM wave is coalesced against the shared lastCrmInvalidationAt
        // timestamp; proposals + agent-feed are NOT coalesced (they have no
        // equivalent gate) — hence the asymmetry below.
        const crmInvalidated = !wasCrmInvalidatedRecently(
          WAKE_REFRESH_COALESCE_MS
        );
        if (crmInvalidated) {
          invalidateCrmQueries(queryClient);
        }

        if (orgId) {
          queryClient.invalidateQueries({
            queryKey: proposalQueryKeys.lists(orgId),
          });
          queryClient.invalidateQueries({
            queryKey: agentFeedQueryKey(orgId),
          });
        }

        logger.info('[activity] tab awake', { orgId, crmInvalidated });
      } else {
        logger.info('[activity] tab awake (no queryClient)', { orgId });
      }
    }

    // Restore focusManager to its default (visibility-based) tracking only
    // AFTER the wake refresh: restoring first emits a focus event that makes
    // refetchOnWindowFocus queries fetch, and the invalidation above would
    // then cancel and repeat them. Never setFocused(true), which would pin
    // focus and diverge from every other consumer.
    focusManager.setFocused(undefined);
    focusPinnedRef.current = false;
  }, [asleep, orgId, queryClient]);

  // If this hook unmounts while asleep, restore default focus tracking so the
  // app is never left with every refetchInterval poller paused. No wake
  // refresh here — nothing is mounted to show it.
  useEffect(() => {
    return () => {
      if (focusPinnedRef.current) {
        focusManager.setFocused(undefined);
        focusPinnedRef.current = false;
      }
    };
  }, []);

  return asleep;
}
